import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Target, AlertCircle, Award, RefreshCw, Lightbulb, Trophy, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function PortfolioAnalyzer({ user, onLogout }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    fetchAnalysis();
    fetchRecommendations();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching portfolio analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/portfolio-recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations(['Unable to fetch recommendations. Please try again later.']);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/portfolio-analyzer" />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Analyzing portfolio...</p>
          </div>
        </div>
      </div>
    );
  }

  const allocationData = analysis?.asset_allocation
    ? Object.entries(analysis.asset_allocation).map(([type, value]) => ({
        name: type.replace('_', ' ').toUpperCase(),
        value: value
      }))
    : [];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="portfolio-analyzer-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/portfolio-analyzer" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Portfolio Analyzer</h1>
          <p className="text-gray-600 dark:text-gray-400">Comprehensive analysis of your investment portfolio</p>
        </div>

        {/* Quick Résumé Banner */}
        {analysis && analysis.annualized_return > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 border border-teal-200 dark:border-teal-700 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <Lightbulb className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Portfolio Insight:</span> Your portfolio is returning{' '}
                  <span className="font-bold text-gray-800 dark:text-gray-200">
                    {analysis.annualized_return.toFixed(2)}% yearly
                  </span>
                  {analysis.benchmark_comparison && analysis.benchmark_comparison.is_outperforming && (
                    <span>
                      , outperforming {analysis.benchmark_comparison.benchmark_name} by{' '}
                      <span className="font-bold text-gray-800 dark:text-gray-200">
                        +{analysis.benchmark_comparison.outperformance.toFixed(2)}%
                      </span>
                    </span>
                  )}
                  {analysis.benchmark_comparison && !analysis.benchmark_comparison.is_outperforming && analysis.annualized_return > 0 && (
                    <span>
                      , trailing {analysis.benchmark_comparison.benchmark_name} by{' '}
                      <span className="font-bold text-gray-800 dark:text-gray-200">
                        {Math.abs(analysis.benchmark_comparison.outperformance).toFixed(2)}%
                      </span>
                    </span>
                  )}
                  {analysis.losses_count > 0 && (
                    <span>
                      . {analysis.losses_count} {analysis.losses_count === 1 ? 'asset needs' : 'assets need'} immediate attention
                    </span>
                  )}
                  {analysis.losses_count === 0 && analysis.below_average_count > 0 && (
                    <span>
                      . {analysis.below_average_count} {analysis.below_average_count === 1 ? 'asset is' : 'assets are'} below portfolio average
                    </span>
                  )}
                  {analysis.losses_count === 0 && analysis.below_average_count === 0 && (
                    <span>
                      . All investments performing well! 🎉
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Value</h3>
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(analysis?.total_value || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invested</h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(analysis?.total_invested || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                {analysis?.total_returns >= 0 ? <TrendingUp className="w-6 h-6 text-white" /> : <TrendingDown className="w-6 h-6 text-white" />}
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</h3>
            <p className={`text-3xl font-bold ${analysis?.total_returns >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {analysis?.total_returns >= 0 ? '+' : ''}{formatCurrency(analysis?.total_returns || 0, user?.currency)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {analysis?.returns_percentage?.toFixed(2)}% absolute
            </p>
            {analysis?.annualized_return !== undefined && analysis?.annualized_return !== 0 && analysis?.holding_period_days > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">Annualized Return (CAGR)</p>
                <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                  {analysis.annualized_return.toFixed(2)}% per year
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Holding period: {Math.floor(analysis.holding_period_days / 30)} months
                </p>
              </div>
            )}
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                analysis?.risk_level === 'High' ? 'from-red-400 to-rose-500' : 
                analysis?.risk_level === 'Medium' ? 'from-yellow-400 to-amber-500' : 
                'from-green-400 to-emerald-500'
              } flex items-center justify-center`}>
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk Level</h3>
            <p className={`text-3xl font-bold ${getRiskColor(analysis?.risk_level)}`}>{analysis?.risk_level || 'N/A'}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Asset Allocation */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Asset Allocation</h2>
            {allocationData.length > 0 ? (
              <>
                <div className="flex justify-center mb-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={allocationData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {allocationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value, user?.currency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {allocationData.map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white dark:bg-gray-700 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-gray-700 dark:text-gray-300 font-medium">{asset.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 block">{formatCurrency(asset.value, user?.currency)}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {((asset.value / allocationData.reduce((sum, a) => sum + a.value, 0)) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">No investment data available</p>
            )}
          </Card>

          {/* Diversification & Performance */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Portfolio Metrics</h2>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Diversification Score</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{analysis?.diversification_score || 0}/100</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-indigo-400 to-purple-500 h-3 rounded-full"
                    style={{ width: `${analysis?.diversification_score || 0}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {analysis?.diversification_score >= 80 ? 'Excellent diversification' :
                   analysis?.diversification_score >= 60 ? 'Good diversification' :
                   analysis?.diversification_score >= 40 ? 'Moderate diversification' :
                   'Consider diversifying more'}
                </p>
              </div>

              {analysis?.top_3_performers && analysis.top_3_performers.length > 0 && (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900 dark:to-green-900 p-6 rounded-xl border border-emerald-200 dark:border-emerald-700">
                  <div className="flex items-center space-x-2 mb-4">
                    <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-lg font-bold text-emerald-800 dark:text-emerald-300">Top 3 Performers</span>
                  </div>
                  <div className="space-y-3">
                    {analysis.top_3_performers.map((performer, index) => (
                      <div key={index} className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </span>
                            <div>
                              <p className="text-gray-900 dark:text-gray-100 font-semibold">{performer.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {formatCurrency(performer.amount, user?.currency)} → {formatCurrency(performer.current_value, user?.currency)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-600 dark:text-emerald-400 font-bold text-lg">
                              +{formatCurrency(performer.returns, user?.currency)}
                            </p>
                            <p className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
                              +{performer.returns_percentage.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Needs Attention Section - Smart Display Logic */}
              {analysis?.losses_count > 0 && analysis?.bottom_3_performers && analysis.bottom_3_performers.length > 0 && (
                <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900 dark:to-red-900 p-6 rounded-xl border border-rose-200 dark:border-rose-700">
                  <div className="flex items-center space-x-2 mb-4">
                    <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                    <span className="text-lg font-bold text-rose-800 dark:text-rose-300">Needs Immediate Attention</span>
                  </div>
                  <p className="text-sm text-rose-700 dark:text-rose-300 mb-4">
                    These investments are showing actual losses. Consider reviewing your strategy.
                  </p>
                  <div className="space-y-3">
                    {analysis.bottom_3_performers.map((performer, index) => (
                      <div key={index} className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg border-l-4 border-rose-500">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">🔴</span>
                            <div>
                              <p className="text-gray-900 dark:text-gray-100 font-semibold">{performer.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {formatCurrency(performer.amount, user?.currency)} → {formatCurrency(performer.current_value, user?.currency)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-rose-600 dark:text-rose-400 font-bold text-lg">
                              {formatCurrency(performer.returns, user?.currency)}
                            </p>
                            <p className="text-rose-600 dark:text-rose-400 text-sm font-semibold">
                              {performer.returns_percentage.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Below Average Performers - Only show if NO losses */}
              {analysis?.losses_count === 0 && analysis?.below_average_count > 0 && analysis?.bottom_3_performers && analysis.bottom_3_performers.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-6 rounded-xl border border-amber-200 dark:border-amber-700">
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingDown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    <span className="text-lg font-bold text-amber-800 dark:text-amber-300">Below Portfolio Average</span>
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                    These investments are profitable but underperforming compared to your portfolio average of {analysis.returns_percentage?.toFixed(2)}%.
                  </p>
                  <div className="space-y-3">
                    {analysis.bottom_3_performers.map((performer, index) => (
                      <div key={index} className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg border-l-4 border-amber-500">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">⚠️</span>
                            <div>
                              <p className="text-gray-900 dark:text-gray-100 font-semibold">{performer.name}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {formatCurrency(performer.amount, user?.currency)} → {formatCurrency(performer.current_value, user?.currency)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-amber-600 dark:text-amber-400 font-bold text-lg">
                              +{formatCurrency(performer.returns, user?.currency)}
                            </p>
                            <p className="text-amber-600 dark:text-amber-400 text-sm font-semibold">
                              +{performer.returns_percentage.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Good Message - Show if no losses AND no below-average performers */}
              {analysis?.losses_count === 0 && analysis?.below_average_count === 0 && (
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-6 rounded-xl border border-emerald-200 dark:border-emerald-700">
                  <div className="flex items-center space-x-2 mb-4">
                    <Award className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-lg font-bold text-emerald-800 dark:text-emerald-300">🎉 Excellent Performance!</span>
                  </div>
                  <p className="text-emerald-700 dark:text-emerald-300">
                    All your investments are performing well and meeting or exceeding your portfolio average. Keep up the great work!
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Recommendations */}
        {/* AI Recommendations */}
        <Card className="glass p-6 dark:bg-gray-800">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-2">
              <Lightbulb className="w-6 h-6 text-amber-500" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">AI-Powered Recommendations</h2>
            </div>
            <Button
              onClick={fetchRecommendations}
              disabled={loadingRecommendations}
              variant="outline"
              size="sm"
              className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 hover:bg-teal-50 dark:hover:bg-gray-600"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingRecommendations ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          
          {loadingRecommendations ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Analyzing your portfolio with AI...</p>
            </div>
          ) : (
            <div className="space-y-5">
              {recommendations.map((recommendation, index) => (
                <div 
                  key={index} 
                  className="group relative"
                >
                  <div className="flex items-start space-x-4 p-5 bg-gradient-to-r from-teal-50 via-emerald-50 to-green-50 dark:from-gray-700 dark:via-gray-700 dark:to-gray-700 rounded-xl border-2 border-teal-100 dark:border-gray-600 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-lg transition-all duration-200">
                    {/* Number Badge */}
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-base shadow-md">
                      {index + 1}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 space-y-2">
                      <p className="text-gray-800 dark:text-gray-200 leading-relaxed text-base">
                        {recommendation}
                      </p>
                    </div>

                    {/* Icon indicator */}
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Lightbulb className="w-5 h-5 text-teal-500 dark:text-teal-400" />
                    </div>
                  </div>
                </div>
              ))}
              
              {recommendations.length === 0 && (
                <div className="text-center py-12">
                  <Lightbulb className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 text-lg">No recommendations available at this time.</p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Try adding more investments to get personalized insights.</p>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start">
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>These recommendations are AI-generated based on your portfolio data. Always consider your personal financial goals and consult with a certified financial advisor before making investment decisions.</span>
            </p>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
